class CartNotification extends HTMLElement {
  constructor() {
    super();

    this.notification = document.getElementById("cart-notification");
    this.header = document.querySelector("sticky-header");
    this.onBodyClick = this.handleBodyClick.bind(this);

    this.notification.addEventListener(
      "keyup",
      (evt) => evt.code === "Escape" && this.close(),
    );
    this.querySelectorAll('button[type="button"]').forEach((closeButton) =>
      closeButton.addEventListener("click", this.close.bind(this)),
    );
  }

  open() {
    this.notification.classList.add("animate", "active");

    this.notification.addEventListener(
      "transitionend",
      () => {
        this.notification.focus();
        trapFocus(this.notification);
      },
      { once: true },
    );

    document.body.addEventListener("click", this.onBodyClick);

    this.dispatchCartViewEvent();
  }

  // The notification's outer element is server-rendered once at page load, so
  // its `cart` Liquid object reflects the pre-add state. The morphed children
  // (inserted from the /cart/add.js sections response in renderContents) are
  // post-add, but they don't expose the full cart shape we need for the event
  // payload. So we keep an explicit /cart.json fetch on open. Migrating to the
  // factory + filter would require re-rendering the notification element
  // itself in sections, which is out of scope for this PR.
  async dispatchCartViewEvent() {
    const { CartViewEvent } = window.StandardEvents || {};
    if (!CartViewEvent) return;

    try {
      const response = await fetch(`${routes.cart_url}.json`);
      const cart = await response.json();
      if (!cart?.currency) return;

      this.dispatchEvent(
        new CartViewEvent({
          context: "dialog",
          cart: CartViewEvent.createCartFromAjaxResponse(cart),
        }),
      );
    } catch (e) {
      // cart:view is informational; swallow fetch errors
    }
  }

  close() {
    this.notification.classList.remove("active");
    document.body.removeEventListener("click", this.onBodyClick);

    removeTrapFocus(this.activeElement);
  }

  renderContents(parsedState) {
    this.cartItemKey = parsedState.key;
    this.itemKeys = parsedState.itemKeys?.length
      ? parsedState.itemKeys
      : parsedState.key
        ? [parsedState.key]
        : [];

    this.getSectionsToRender().forEach((section) => {
      const sectionElement = document.getElementById(section.id);
      if (!sectionElement || !parsedState.sections?.[section.id]) return;

      const html = this.getSectionInnerHTML(
        parsedState.sections[section.id],
        section,
      );
      if (html === null) return;
      sectionElement.innerHTML = html;
    });

    this.setHeading(parsedState.bundleTitle);
    if (this.header) this.header.reveal();
    this.open();
  }

  getSectionsToRender() {
    return [
      {
        id: "cart-notification-product",
        selector: `[id="cart-notification-product-${this.cartItemKey}"]`,
        itemKeys: this.itemKeys,
      },
      {
        id: "cart-notification-button",
      },
      {
        id: "cart-icon-bubble",
      },
    ];
  }

  getSectionInnerHTML(html, section = {}) {
    if (!html) return null;
    const doc = new DOMParser().parseFromString(html, "text/html");

    if (section.itemKeys?.length) {
      const parts = section.itemKeys
        .map((key) => {
          const el = doc.getElementById(`cart-notification-product-${key}`);
          return el ? el.outerHTML : "";
        })
        .filter(Boolean);
      if (parts.length) return parts.join("");
    }

    const selector = section.selector || ".shopify-section";
    const parsed = doc.querySelector(selector);
    return parsed ? parsed.innerHTML : null;
  }

  setHeading(bundleTitle) {
    const headingText = this.querySelector("[data-cart-notification-heading]");
    if (!headingText) return;

    if (!this.defaultHeading) {
      this.defaultHeading = headingText.textContent.trim();
    }

    if (bundleTitle) {
      const template =
        window.cartStrings?.bundleAdded ||
        window.bundleVolumeStrings?.bundleAdded ||
        "Bundle added: {{ name }}";
      headingText.textContent = template.replace("{{ name }}", bundleTitle);
    } else {
      headingText.textContent = this.defaultHeading;
    }
  }

  handleBodyClick(evt) {
    const target = evt.target;
    if (target !== this.notification && !target.closest("cart-notification")) {
      const disclosure = target.closest("details-disclosure, header-menu");
      this.activeElement = disclosure
        ? disclosure.querySelector("summary")
        : null;
      this.close();
    }
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define("cart-notification", CartNotification);
